import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';
import type { Control } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

interface CourtInfoFieldsProps {
  // eslint_disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
}

export function CourtPartyFields({ control }: CourtInfoFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex gap-2.5 flex-wrap">
      <FormField
        control={control}
        name="clientStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('status.status')}</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEFENDANT">{t('defendant')}</SelectItem>
                  <SelectItem value="PLAINTIFF">{t('plainTiff')}</SelectItem>
                  <SelectItem value="SUSPECT">{t('suspect')}</SelectItem>
                  <SelectItem value="ACCUSED">{t('accused')}</SelectItem>
                  <SelectItem value="WITNESS">{t('witness')}</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="defendant"
        render={({ field }) => (
          <FormItem className="flex-1 min-w-[150px]">
            <FormLabel>{t('defendant')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="plainTiff"
        render={({ field }) => (
          <FormItem className="flex-1 min-w-[150px]">
            <FormLabel>{t('plainTiff')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

export function CourtDetailsFields({ control }: CourtInfoFieldsProps) {
  const { t } = useTranslation();

  return (
    <div>
      <Separator />
      <h1 className="text-xl font-semibold mb-2">{t('courtInfo')}</h1>
      <div className="flex gap-2.5 flex-wrap">
        <FormField
          control={control}
          name="judgeName"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[150px]">
              <FormLabel>{t('judgeName')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="courtName"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[150px]">
              <FormLabel>{t('courtName')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="courtEmail"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[150px]">
              <FormLabel>{t('email')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="courtPhone"
          render={({ field }) => (
            <FormItem className="flex-1 min-w-[150px]">
              <FormLabel>{t('phone')}</FormLabel>
              <FormControl>
                <PatternFormat
                  format="+998 (##) ### ## ##"
                  allowEmptyFormatting
                  mask="_"
                  customInput={Input}
                  value={field.value ?? ''}
                  // An emptied input must store '' — the bare mask
                  // ("+998 (__) ___ __ __") would fail the phone validation.
                  onValueChange={(values) =>
                    field.onChange(values.value ? values.formattedValue : '')
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
